import React, { useState, useMemo } from 'react';
import Hero from '../components/ui/Hero';
import AnimatronicGrid from '../components/animatronics/AnimatronicGrid';
import AnimatronicFilters from '../components/filters/AnimatronicFilters';
import ScrollingBanner from '../components/sponsors/ScrollingBanner/Banner';
import SortSelect from '../components/filters/SortSelect';
import { useAnimatronics } from '../hooks/useAnimatronics';
import { useSortedAnimatronics } from '../hooks/useSortedAnimatronics';
import type { SortOption } from '../components/filters/SortSelect';
import { AlertTriangle } from 'lucide-react';

const DatabasePage = () => {
  const [filters, setFilters] = useState({
    search: '',
    country: '',
    park: '',
    attraction: '',
    type: '',
    status: '',
    brand: ''
  });

  const [sortBy, setSortBy] = useState<SortOption>('favorite');
  const [reverse, setReverse] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { 
    animatronics, 
    isLoading, 
    error,
    retry,
    uniqueCountries, 
    uniqueParks, 
    uniqueAttractions, 
    uniqueTypes,
    uniqueStatuses,
    uniqueBrands
  } = useAnimatronics();

  // Filter animatronics
  const filteredAnimatronics = useMemo(() => {
    return animatronics.filter(animatronic => {
      const matchesSearch = !filters.search || 
        animatronic.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        animatronic.description.toLowerCase().includes(filters.search.toLowerCase());
      
      const matchesCountry = !filters.country || animatronic.country === filters.country;
      const matchesPark = !filters.park || animatronic.park === filters.park;
      const matchesAttraction = !filters.attraction || animatronic.attraction === filters.attraction;
      const matchesType = !filters.type || animatronic.type === filters.type;
      const matchesStatus = !filters.status || animatronic.status === filters.status;
      const matchesBrand = !filters.brand || animatronic.brand === filters.brand;

      return matchesSearch && matchesCountry && matchesPark && 
             matchesAttraction && matchesType && matchesStatus && matchesBrand;
    });
  }, [animatronics, filters]);

  const sortedAnimatronics = useSortedAnimatronics(filteredAnimatronics, sortBy, reverse);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy, reverse]);

  const heroImage = "https://nifmkzddenyukgjcdetc.supabase.co/storage/v1/object/public/animatronics_data/logo_light.webp";

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-500/10 text-red-500 p-6 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium mb-1">Error Loading Animatronics</h3>
            <p className="text-sm mb-4">{error.message}</p>
            <button
              onClick={retry}
              className="text-sm px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Hero
        title="Animatronics Database"
        description="Browse our comprehensive collection of animatronic figures from around the world."
        backgroundImage={heroImage}
        compact
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatronicFilters
          filters={filters}
          onSearchChange={(value) => setFilters(prev => ({ ...prev, search: value }))}
          onCountryChange={(value) => setFilters(prev => ({ ...prev, country: value }))}
          onParkChange={(value) => setFilters(prev => ({ ...prev, park: value }))}
          onAttractionChange={(value) => setFilters(prev => ({ ...prev, attraction: value }))}
          onTypeChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
          onStatusChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
          onBrandChange={(value) => setFilters(prev => ({ ...prev, brand: value }))}
          countries={uniqueCountries}
          parks={uniqueParks}
          attractions={uniqueAttractions}
          types={uniqueTypes}
          statuses={uniqueStatuses}
          brands={uniqueBrands}
        />

        <div className="flex justify-end mb-6">
          <SortSelect 
            value={sortBy} 
            onChange={setSortBy}
            reverse={reverse}
            onReverseChange={setReverse}
          />
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-content-secondary">
            Loading animatronics...
          </div>
        ) : (
          <AnimatronicGrid
            animatronics={sortedAnimatronics}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            itemsPerPage={12}
          />
        )}

        {/*  <div className="mt-12">
          <ScrollingBanner />
        </div>*/}
      </div>
    </div>
  );
};

export default DatabasePage;